import { browserSupportsWebAuthn, startAuthentication, startRegistration } from '@simplewebauthn/browser';
import { ApiError } from './api';

// Security-key ceremonies for approvals. The browser shows its own prompt; the
// server verifies the result against a single-use challenge bound to the job.
export const webauthnAvailable = () => typeof window !== 'undefined' && window.isSecureContext && browserSupportsWebAuthn();

export function describeKeyError(error: unknown) {
  const name = (error as { name?: string })?.name;
  if (name === 'NotAllowedError' || name === 'AbortError') return 'The security key prompt was cancelled or timed out.';
  if (name === 'InvalidStateError') return 'That key is already registered.';
  if (name === 'SecurityError') return 'Security keys need a secure address: open the console at localhost or over HTTPS.';
  return 'The security key could not be used. Try again.';
}

// The server's options are WebAuthn JSON; the library validates their shape.
export const signWithKey = (options: Record<string, unknown>) => startAuthentication({ optionsJSON: options as never });
export const createKey = (options: Record<string, unknown>) => startRegistration({ optionsJSON: options as never });

/**
 * Signs in with a registered key when Google is unavailable. Plain fetches, not the API client: a refusal here
 * is a sign-in failure, not a session ending. Resolves once the server has set the session cookie.
 */
export async function signInWithKey() {
  const post = (path: string, body?: unknown) => fetch(path, { method: 'POST', credentials: 'same-origin', cache: 'no-store', redirect: 'error',
    headers: body === undefined ? {} : { 'content-type': 'application/json' }, body: body === undefined ? undefined : JSON.stringify(body) });
  const options = await post('/auth/key/options');
  if (!options.ok) throw new ApiError(options.status);
  const done = await post('/auth/key/verify', await signWithKey(await options.json()));
  if (!done.ok) throw new ApiError(done.status);
}
